import { useState } from "react";
import { api, type AgentOsDraftStatus, type AgentOsLoopExecutionReceipt } from "../api";
import { ActionButton, FormField, GraphView, StateMessage, StatusBadge, Timeline } from "../design-system";
import { AgentOsDraftFlow } from "./AgentOsDraftFlow";
import { recordLabel, recordStatus, type AgentOsStudioRecord } from "./agentOsStudioModel";

type LoopStep = { id: string; action: string };
type LoopConfig = {
  schemaVersion?: unknown;
  steps?: unknown;
  maxIterations?: unknown;
  timeBudgetSeconds?: unknown;
  tokenBudget?: unknown;
  retryStrategy?: unknown;
  approvalPoints?: unknown;
};

function loopConfig(record: AgentOsStudioRecord): LoopConfig | null {
  if (record.config && typeof record.config === "object" && !Array.isArray(record.config)) return record.config as LoopConfig;
  if (typeof record.configJson !== "string") return null;
  try {
    const parsed = JSON.parse(record.configJson) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as LoopConfig : null;
  } catch {
    return null;
  }
}

function loopSteps(value: unknown): LoopStep[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item, index) => {
    if (typeof item === "string" && item.trim()) return [{ id: item.trim(), action: "legacy-read-only" }];
    if (!item || typeof item !== "object" || Array.isArray(item)) return [];
    const candidate = item as { id?: unknown; action?: unknown };
    if (typeof candidate.id !== "string" || !candidate.id.trim() || typeof candidate.action !== "string" || !candidate.action.trim()) return [];
    return [{ id: candidate.id.trim() || `step-${index + 1}`, action: candidate.action.trim() }];
  });
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0) : [];
}

function retryAttempts(value: unknown): string {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && !Array.isArray(value) && Number.isInteger((value as { maxAttempts?: unknown }).maxAttempts)) {
    return `maxAttempts: ${String((value as { maxAttempts: number }).maxAttempts)}`;
  }
  return "—";
}

interface RoutineLoopBuilderProps {
  section?: "routines" | "loops";
  routines: AgentOsStudioRecord[];
  loops: AgentOsStudioRecord[];
  selectedRecord?: AgentOsStudioRecord | null;
  enabled?: boolean;
  mutationEnabled?: boolean;
  createEnabled?: boolean;
}

export function RoutineLoopBuilder({ section, routines, loops, selectedRecord, enabled = false, mutationEnabled = enabled, createEnabled = enabled }: RoutineLoopBuilderProps): JSX.Element {
  const [routineReceipt, setRoutineReceipt] = useState<{ runId: number; status: string } | null>(null);
  const [loopReceipt, setLoopReceipt] = useState<AgentOsLoopExecutionReceipt | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeSection = section ?? (routines.length > 0 ? "routines" : "loops");
  const routine = activeSection === "routines" ? selectedRecord ?? routines[0] ?? null : routines[0] ?? null;
  const loop = activeSection === "loops" ? selectedRecord ?? loops[0] ?? null : loops[0] ?? null;
  const [createMode, setCreateMode] = useState(false);
  const [draftStatus, setDraftStatus] = useState<AgentOsDraftStatus | null>(null);
  const [newId, setNewId] = useState(activeSection === "routines" ? "owner-routine" : "owner-loop");
  const [label, setLabel] = useState("");
  const [prompt, setPrompt] = useState("");
  const [scheduleHour, setScheduleHour] = useState(8);
  const [scheduleMinute, setScheduleMinute] = useState(0);
  const [targetEnabled, setTargetEnabled] = useState(Boolean((activeSection === "routines" ? routine : loop)?.enabled));
  const [loopRoutineId, setLoopRoutineId] = useState(typeof routine?.id === "string" ? routine.id : "");
  const [maxAttempts, setMaxAttempts] = useState(2);
  const [approvalBeforeRun, setApprovalBeforeRun] = useState(true);
  const formLocked = draftStatus !== null;

  async function runRoutine(): Promise<void> {
    if (!routine || typeof routine.id !== "string") return;
    setBusy(true); setError(null);
    try {
      const result = await api.runRoutineNow(routine.id);
      if ("error" in result) throw new Error(result.error);
      setRoutineReceipt({ runId: result.runId, status: "completed" });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Routine konnte nicht ausgeführt werden.");
    } finally {
      setBusy(false);
    }
  }

  async function runLoop(): Promise<void> {
    if (!loop || typeof loop.id !== "string") return;
    setBusy(true); setError(null);
    try {
      const result = await api.agentOsLoopRun(loop.id);
      setLoopReceipt(result.receipt);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Loop konnte nicht ausgeführt werden.");
    } finally {
      setBusy(false);
    }
  }

  async function dryRunLoop(): Promise<void> {
    if (!loop || typeof loop.id !== "string") return;
    setBusy(true); setError(null);
    try {
      const result = await api.agentOsLoopDryRun(loop.id);
      setLoopReceipt(result.receipt);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Loop-Dry-Run ist fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  async function approveAndResumeLoop(): Promise<void> {
    if (!loopReceipt?.approvalId || loopReceipt.status !== "awaiting_approval") return;
    setBusy(true); setError(null);
    try {
      const result = await api.agentOsLoopApproveAndResume(loopReceipt.runId, loopReceipt.approvalId);
      setLoopReceipt(result.receipt);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Loop-Freigabe oder Fortsetzung ist fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  async function recoverLoop(): Promise<void> {
    if (!loopReceipt || loopReceipt.status !== "failed") return;
    setBusy(true); setError(null);
    try {
      const result = await api.agentOsLoopRecover(loopReceipt.runId);
      setLoopReceipt(result.receipt);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Loop-Recovery ist fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  const selected = activeSection === "routines" ? routine : loop;
  const selectedId = typeof selected?.id === "string" ? selected.id : null;
  const selectedBaseHash = typeof selected?.mutationBaseHash === "string" ? selected.mutationBaseHash : null;
  const routineCreateValue = {
    id: newId.trim(),
    label: label.trim(),
    triggerType: "schedule",
    triggerConfigJson: JSON.stringify({ type: "schedule", hour: scheduleHour, minute: scheduleMinute, weekdays: [] }),
    prompt: prompt.trim(),
    enabled: false,
  };
  const loopConfigValue = {
    schemaVersion: "v1",
    steps: [
      { id: "safe-checkpoint", action: "checkpoint" },
      { id: "run-routine", action: `routine.run:${loopRoutineId.trim()}` },
    ],
    maxIterations: 4,
    timeBudgetSeconds: 300,
    tokenBudget: 0,
    retryStrategy: { maxAttempts },
    approvalPoints: approvalBeforeRun ? ["run-routine"] : [],
  };
  const loopCreateValue = { id: newId.trim(), label: label.trim(), configJson: JSON.stringify(loopConfigValue), enabled: false };
  const mutationPayload = createMode
    ? { operation: "create", value: activeSection === "routines" ? routineCreateValue : loopCreateValue }
    : { enabled: targetEnabled };
  const mutationEntityId = createMode ? newId.trim() : selectedId;
  const mutationBaseHash = createMode ? null : selectedBaseHash;
  const laneEnabled = createMode ? createEnabled : mutationEnabled;

  return (
    <div className="space-y-3" data-routine-loop-builder>
      <section className="rounded-lg border border-jarvis-accent/25 bg-jarvis-accent/[0.04] p-3" aria-label={activeSection === "routines" ? "Visueller Routine Builder" : "Visueller Loop Builder"}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] text-jarvis-accent">{activeSection === "routines" ? "Routine konfigurieren" : "Loop konfigurieren"}</p>
            <p className="mt-1 text-xs text-jarvis-muted">Jede Änderung läuft als deaktivierter Draft durch Validate, Simulate, Diff, Owner-Freigabe, Git-Apply, Audit und Rollback.</p>
          </div>
          <ActionButton disabled={!createEnabled || formLocked} onClick={() => { setCreateMode((current) => !current); setDraftStatus(null); setTargetEnabled(false); }}>
            {createMode ? "Bestehendes Objekt bearbeiten" : activeSection === "routines" ? "Neue Routine" : "Neuer Loop"}
          </ActionButton>
        </div>
        {createMode ? (
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <FormField id={`${activeSection}-new-id`} label="Technischer Schlüssel" help="Kleinbuchstaben, Zahlen, Punkt, Unterstrich oder Bindestrich.">
              <input value={newId} disabled={formLocked} onChange={(event) => setNewId(event.target.value)} />
            </FormField>
            <FormField id={`${activeSection}-new-label`} label="Anzeigename">
              <input value={label} disabled={formLocked} onChange={(event) => setLabel(event.target.value)} />
            </FormField>
            {activeSection === "routines" ? <>
              <FormField id="routine-prompt" label="Auftrag" help="Der Auftrag wird erst nach Apply ausführbar.">
                <textarea value={prompt} disabled={formLocked} onChange={(event) => setPrompt(event.target.value)} />
              </FormField>
              <div className="grid grid-cols-2 gap-2">
                <FormField id="routine-hour" label="Stunde (UTC)"><input type="number" min={0} max={23} value={scheduleHour} disabled={formLocked} onChange={(event) => setScheduleHour(Number(event.target.value))} /></FormField>
                <FormField id="routine-minute" label="Minute"><input type="number" min={0} max={59} value={scheduleMinute} disabled={formLocked} onChange={(event) => setScheduleMinute(Number(event.target.value))} /></FormField>
              </div>
            </> : <>
              <FormField id="loop-routine" label="Routine" help="Der Loop ruft diese kanonische Routine über den echten Scheduler auf.">
                <select value={loopRoutineId} disabled={formLocked} onChange={(event) => setLoopRoutineId(event.target.value)}>
                  <option value="">Routine auswählen</option>
                  {routines.map((candidate) => <option key={String(candidate.id)} value={String(candidate.id)}>{recordLabel(candidate)}</option>)}
                </select>
              </FormField>
              <FormField id="loop-attempts" label="Maximale Versuche"><input type="number" min={1} max={10} value={maxAttempts} disabled={formLocked} onChange={(event) => setMaxAttempts(Number(event.target.value))} /></FormField>
              <label className="flex min-h-11 items-center gap-2 rounded-md px-2 text-xs text-jarvis-muted"><input type="checkbox" checked={approvalBeforeRun} disabled={formLocked} onChange={(event) => setApprovalBeforeRun(event.target.checked)} /> Owner-Freigabe vor Routine-Ausführung</label>
            </>}
          </div>
        ) : selected ? (
          <label className="mt-3 flex min-h-11 items-center gap-2 rounded-md border border-white/10 bg-black/15 p-2 text-xs text-jarvis-muted">
            <input type="checkbox" checked={targetEnabled} disabled={formLocked || !mutationEnabled} onChange={(event) => setTargetEnabled(event.target.checked)} />
            {targetEnabled ? "Aktivierung als versionierte Änderung vorbereiten" : "Pausierung als versionierte Änderung vorbereiten"}
          </label>
        ) : <StateMessage label="Noch kein Objekt">Lege einen deaktivierten Draft an; Aktivierung folgt in einer separaten freigegebenen Änderung.</StateMessage>}
        {(createMode || selected) && <div className="mt-3"><AgentOsDraftFlow
          key={`${activeSection}:${mutationEntityId ?? "none"}:${mutationBaseHash ?? "new"}:${JSON.stringify(mutationPayload)}`}
          entityType={activeSection === "routines" ? "routine" : "loop"}
          entityId={mutationEntityId}
          baseHash={mutationBaseHash}
          initialPayload={mutationPayload}
          enabled={laneEnabled}
          editorMode="guided"
          onDraftStatus={setDraftStatus}
        /></div>}
      </section>

      {activeSection === "routines" && <section className="rounded-lg border border-white/10 bg-black/15 p-3" aria-label="Routine Builder">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.14em] text-jarvis-accent">Routine Builder</p>
            <p className="mt-1 text-xs font-medium text-jarvis-text">{routine ? recordLabel(routine) : "Keine Routine ausgewählt"}</p>
            <p className="mt-1 text-[11px] text-jarvis-muted">Echter RoutineScheduler → AgentGraph → Common Harness. Draft und Aktivierung bleiben in der Unified Change Lane darüber.</p>
          </div>
          <ActionButton variant="success" disabled={!enabled || busy || !routine} onClick={() => void runRoutine()} aria-busy={busy || undefined}>Routine jetzt ausführen</ActionButton>
        </div>
        {routine && <div className="mt-2"><StatusBadge status={recordStatus(routine)} label={recordStatus(routine)} /></div>}
        {routineReceipt && <div className="mt-3"><Timeline label="Routine Run" events={[{ id: String(routineReceipt.runId), at: new Date().toISOString(), displayAt: "aktueller Lauf", label: "RoutineScheduler abgeschlossen", status: routineReceipt.status, evidenceRef: `routine-run:${routineReceipt.runId}` }]} /></div>}
      </section>}

      {activeSection === "loops" && <section className="rounded-lg border border-jarvis-accent/20 bg-jarvis-accent/[0.04] p-3" aria-label="Loop Builder">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.14em] text-jarvis-accent">Loop Designer</p>
            <p className="mt-1 text-xs font-medium text-jarvis-text">{loop ? recordLabel(loop) : "Kein Loop ausgewählt"}</p>
            <p className="mt-1 text-[11px] text-jarvis-muted">Reale Schritte, begrenzte Retries, Iterations-/Zeit-/Token-Budgets und Recovery ab dem letzten sicheren Checkpoint.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <ActionButton disabled={!enabled || busy || !loop} onClick={() => void dryRunLoop()} aria-busy={busy || undefined}>Dry Run</ActionButton>
            <ActionButton variant="success" disabled={!enabled || busy || !loop} onClick={() => void runLoop()} aria-busy={busy || undefined}>Loop ausführen</ActionButton>
            {loopReceipt?.status === "awaiting_approval" && loopReceipt.approvalId && <ActionButton variant="warning" disabled={!enabled || busy} onClick={() => void approveAndResumeLoop()} aria-busy={busy || undefined}>Freigeben und fortsetzen</ActionButton>}
            {loopReceipt?.status === "failed" && <ActionButton variant="primary" disabled={!enabled || busy || !loopReceipt.lastCheckpointRef} onClick={() => void recoverLoop()} aria-busy={busy || undefined}>Recovery starten</ActionButton>}
          </div>
        </div>
        {loop && (() => {
          const config = loopConfig(loop);
          const steps = loopSteps(config?.steps);
          const approvals = stringList(config?.approvalPoints);
          const nodes = steps.map((step, index) => ({ id: `step-${index}`, label: `${step.id} · ${step.action}` }));
          const edges = steps.slice(1).map((_step, index) => ({ from: `step-${index}`, to: `step-${index + 1}`, label: "führt zu" }));
          return (
            <div className="mt-3" data-loop-designer>
              {steps.length > 0 ? <GraphView label="Loop Flow Graph" nodes={nodes} edges={edges} /> : <StateMessage label="Keine ausführbaren Schritte">Der kanonische Readback enthält noch keinen versionierten v1-Ablauf.</StateMessage>}
              <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] text-jarvis-muted">
                <div><dt className="inline text-jarvis-accent">maxIterations:</dt> <dd className="inline">{String(config?.maxIterations ?? "—")}</dd></div>
                <div><dt className="inline text-jarvis-accent">timeBudgetSeconds:</dt> <dd className="inline">{String(config?.timeBudgetSeconds ?? "—")}</dd></div>
                <div><dt className="inline text-jarvis-accent">tokenBudget:</dt> <dd className="inline">{String(config?.tokenBudget ?? "—")}</dd></div>
                <div><dt className="inline text-jarvis-accent">retryStrategy:</dt> <dd className="inline">{retryAttempts(config?.retryStrategy)}</dd></div>
                <div className="col-span-2"><dt className="inline text-jarvis-accent">approvalPoints:</dt> <dd className="inline">{approvals.length > 0 ? approvals.join(", ") : "—"}</dd></div>
              </dl>
            </div>
          );
        })()}
        {loopReceipt && <div className="mt-3 space-y-2">
          <div className="flex flex-wrap gap-2">
            <StatusBadge status={loopReceipt.status} label={loopReceipt.status} />
            <span className="text-[10px] text-jarvis-muted">{loopReceipt.executionMode} · Iterationen {loopReceipt.iterations} · Token {loopReceipt.tokenUsage} · Checkpoint {loopReceipt.lastCheckpointRef ?? "keiner"}</span>
          </div>
          {loopReceipt.recoveredFromRunId && (
            <p className="text-[10px] text-jarvis-muted">Recovery aus {loopReceipt.recoveredFromRunId}</p>
          )}
          {loopReceipt.resumedFromRunId && (
            <p className="text-[10px] text-jarvis-muted">Fortgesetzt aus {loopReceipt.resumedFromRunId}</p>
          )}
          <Timeline label="Loop Run" events={[{ id: loopReceipt.runId, at: new Date().toISOString(), displayAt: "aktueller Lauf", label: loopReceipt.reasonCode ?? loopReceipt.status, status: loopReceipt.status, evidenceRef: loopReceipt.lastCheckpointRef ?? `loop-run:${loopReceipt.runId}` }]} />
        </div>}
        <p className="mt-2 text-[10px] text-jarvis-muted">Recovery ist nur nach einem fail-closed Lauf mit sicherem Checkpoint aktiv. Owner-Modus bleibt read-only.</p>
      </section>}

      {!routine && !loop && <StateMessage label="Keine Routinen oder Loops">Der kanonische Readback enthält keine ausführbaren Objekte.</StateMessage>}
      {!enabled && <StateMessage label="Builder gesperrt">Builder-/Expert-Modus erforderlich; Owner-Modus bleibt read-only.</StateMessage>}
      {error && <StateMessage kind="error" label="Routine-/Loop-Ausführung fehlgeschlagen">{error}</StateMessage>}
    </div>
  );
}
